import { type HTMLAttributes, forwardRef } from "react";

import Icon from "@/components/Icon/Icon";
import { cn } from "@/lib/cn";

import { HStack } from "../Layout";
import { Overlay } from "../Loading/Overlay";

/**
 * MenuItemコンポーネント
 *
 * ドロップダウンメニューやコンテキストメニューの項目として使用するコンポーネント。
 *
 * 使用例：
 * ```tsx
 * <MenuItem>通常のメニュー項目</MenuItem>
 * <MenuItem variant="destructive" disabled>削除</MenuItem>
 * <MenuItem active>選択中の項目</MenuItem>
 * ```
 */

type MenuItemVariant = "default" | "destructive";

interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  active?: boolean;
  isLoading?: boolean;
  /** ローディング表示方法
   * - inline: アイコンのみのインラインローディング（default）
   * - overlay: オーバーレイ付きのフルスクリーンローディング
   */
  loadingType?: "inline" | "overlay";
  variant?: MenuItemVariant;
}

const getMenuItemStyles = ({
  variant,
  active,
}: {
  variant: MenuItemVariant;
  active?: boolean;
}) => {
  const baseStyles = [
    "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden",
    "transition-colors duration-200",
    "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
  ];

  const variantStyles: Record<MenuItemVariant, string[]> = {
    default: [
      "hover:bg-primary-muted-fill hover:text-primary-on-muted-fill",
      "focus-visible:bg-primary-muted-fill focus-visible:text-primary-on-muted-fill",
    ],
    destructive: [
      "hover:bg-destructive hover:text-destructive-foreground",
      "focus-visible:bg-destructive focus-visible:text-destructive-foreground",
    ],
  };

  const activeStyles = active
    ? ["bg-primary-muted-fill/50 text-primary-on-muted-fill"]
    : [];

  return [...baseStyles, ...variantStyles[variant], ...activeStyles];
};

const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      className,
      disabled,
      active,
      isLoading,
      loadingType = "inline",
      variant = "default",
      children,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        getMenuItemStyles({ variant, active }),
        "relative",
        className,
      )}
      data-disabled={disabled || isLoading}
      role="menuitem"
      tabIndex={disabled || isLoading ? -1 : 0}
      {...props}
    >
      {isLoading && loadingType === "overlay" && (
        <Overlay>
          <Icon iconName="Loader2Icon" className="animate-spin" />
        </Overlay>
      )}
      <HStack className={cn("grow items-center gap-2")}>
        {isLoading && loadingType === "inline" && (
          <Icon iconName="Loader2Icon" className="animate-spin" />
        )}
        {children}
      </HStack>
    </div>
  ),
);

MenuItem.displayName = "MenuItem";

export { MenuItem };
export type { MenuItemProps, MenuItemVariant };
